// apps/admin/src/pages/whitelist/components/AddToWhitelistForm.tsx
//
// Open direct card payment for one merchant, from the list itself.
//
// Writes through the SAME endpoint the merchant-detail card uses —
// `PUT /admin/merchants/:id/card-payment-permission`. One write path for a
// money-moving permission is the point: a second one would be a second place
// for the full-shape rule below to be got wrong.
//
// That rule: the PUT REPLACES the permission document. A body of just
// `{ enabled: true }` therefore writes both ceilings as `null`, which means
// UNLIMITED card spend — the exact hole that shipped once already. So the
// ceilings are part of this form rather than a follow-up edit, and the body
// always carries all three fields.
//
// Ceilings are typed in dollars and sent in cents, reusing `dollarsToCents`
// from CapabilityToggleCard so "blank = no limit" and "garbage = refuse to
// save" behave identically on both surfaces.

import { useState, type JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, MerchantPicker, Spinner, useToast } from '@printway/ui';

import { apiClient, ApiError } from '../../../lib/apiClient.js';
import { dollarsToCents } from '../../merchants/components/CapabilityToggleCard.js';
import { newIdempotencyKey } from '../../../lib/idempotencyKey.js';

type TFn = (key: string, vars?: Record<string, unknown>) => string;

export interface AddToWhitelistFormProps {
  /**
   * Merchants on the CURRENT page of the list. A fast local "already
   * listed" answer only — it cannot see other pages or a filtered-out row,
   * so it is a convenience, never the guard. The guard is the server read in
   * `handleAdd`.
   */
  existingIds: ReadonlySet<string>;
  /**
   * PREFIX of the whitelist list keys, invalidated after a successful add.
   * A prefix rather than one exact key: every page and search of the list
   * gains a row, not just the view currently on screen.
   */
  listQueryKey: readonly unknown[];
  t: TFn;
}

export function AddToWhitelistForm(props: AddToWhitelistFormProps): JSX.Element {
  const { existingIds, listQueryKey, t } = props;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [merchantId, setMerchantId] = useState<string | null>(null);
  const [merchantLabel, setMerchantLabel] = useState<string | null>(null);
  const [perOrder, setPerOrder] = useState('');
  const [perDay, setPerDay] = useState('');
  // A blank ceiling means UNLIMITED card spend — legitimate, but never by
  // leaving a box empty without meaning it.
  const perOrderBlank = perOrder.trim() === '';
  const perDayBlank = perDay.trim() === '';
  const anyBlank = perOrderBlank || perDayBlank;
  // The tick is for a SPECIFIC set of blanks. Stored as a boolean and reset
  // only once both boxes were filled, it survived a blank moving from one
  // box to the other — so per-day went out unlimited on a confirmation given
  // for per-order. Comparing the signature voids it on the render the set
  // changes.
  const blankSignature = anyBlank
    ? `${perOrderBlank ? 'order' : ''}|${perDayBlank ? 'day' : ''}`
    : '';
  const [confirmedFor, setConfirmedFor] = useState(blankSignature);
  const [ticked, setTicked] = useState(false);
  // Any change voids the tick — including moving back to a set that was
  // ticked earlier: the operator has to agree again to what is on screen now.
  if (confirmedFor !== blankSignature) {
    setConfirmedFor(blankSignature);
    setTicked(false);
  }
  const noLimitConfirmed = blankSignature !== '' && ticked;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAdd(): Promise<void> {
    if (!merchantId) {
      setError(t('whitelist.merchants.add.pickMerchant'));
      return;
    }
    if (existingIds.has(merchantId)) {
      // Said beside the picker, not in a toast: the thing to fix is the
      // selection sitting right above this line.
      setError(t('whitelist.merchants.add.alreadyListed'));
      return;
    }

    // `undefined` means "not a usable amount" — refuse rather than send
    // `null`, which would silently mean "no limit".
    const perOrderCents = dollarsToCents(perOrder);
    const perDayCents = dollarsToCents(perDay);
    // $0 likewise: the server reads a stored 0 as "no ceiling".
    if (
      perOrderCents === undefined ||
      perDayCents === undefined ||
      perOrderCents === 0 ||
      perDayCents === 0
    ) {
      setError(t('whitelist.merchants.add.badAmount'));
      return;
    }
    if ((perOrderCents === null || perDayCents === null) && !noLimitConfirmed) {
      setError(t('whitelist.merchants.add.noLimitRequired'));
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      await apiClient.put(
        `/admin/merchants/${encodeURIComponent(merchantId)}/card-payment-permission`,
        {
          enabled: true,
          max_per_order_cents: perOrderCents,
          max_per_day_cents: perDayCents,
          // The server refuses (409) if the merchant is already enabled, in
          // the same statement as the write. `existingIds` only knows the page
          // on screen, and a client-side read-then-write raced a concurrent
          // add — either way this PUT replaced ceilings someone had set.
          add_only: true,
        },
        { headers: { 'Idempotency-Key': newIdempotencyKey() } },
      );
      // Independent refreshes — the list, its audit log, and the merchant-detail
      // card that reads the SAME permission under its own key (without that
      // one, opening the merchant after adding them showed the pre-add state
      // from cache, and a Save there wrote that stale shape back).
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: listQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'whitelist', 'log'] }),
        queryClient.invalidateQueries({
          queryKey: ['admin', 'merchants', merchantId, 'card-payment-permission'],
        }),
      ]);
      setMerchantId(null);
      setMerchantLabel(null);
      setPerOrder('');
      setPerDay('');
      setTicked(false);
      toast({ variant: 'success', title: t('whitelist.merchants.add.added') });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ALREADY_WHITELISTED') {
        // Beside the picker: the selection is what needs changing.
        setError(t('whitelist.merchants.add.alreadyListed'));
        return; // `finally` clears `submitting`.
      }
      // Not field-level (the input was valid; the write failed) → toast.
      toast({ variant: 'error', title: t('whitelist.merchants.add.failed') });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="rounded border border-pw-border bg-pw-surface p-4"
      data-testid="whitelist-add-form"
    >
      <h3 className="text-sm font-semibold text-pw-text">
        {t('whitelist.merchants.add.heading')}
      </h3>
      <p className="mt-1 text-xs text-pw-text-muted">
        {t('whitelist.merchants.add.hint')}
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="min-w-64">
          <label
            htmlFor="whitelist-add-merchant"
            className="mb-1 block text-xs font-medium text-pw-text-muted"
          >
            {t('whitelist.merchants.add.merchantLabel')}
          </label>
          <MerchantPicker
            value={merchantId}
            valueLabel={merchantLabel}
            onChange={(id, label) => {
              setMerchantId(id);
              setMerchantLabel(label);
              if (error) setError(null);
            }}
            disabled={submitting}
            testId="whitelist-add-merchant"
            // The picker ships English fallbacks; without these a vi/fr
            // operator gets a half-translated dialog.
            labels={{
              title: t('whitelist.merchants.picker.title'),
              searchPlaceholder: t('whitelist.merchants.picker.searchPlaceholder'),
              loading: t('whitelist.merchants.picker.loading'),
              noResults: t('whitelist.merchants.picker.noResults'),
              prompt: t('whitelist.merchants.picker.prompt'),
            }}
          />
        </div>

        <label className="flex flex-col gap-1 text-xs text-pw-text">
          <span className="text-pw-text-muted">
            {t('whitelist.merchants.add.perOrderLabel')}
          </span>
          <Input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={perOrder}
            disabled={submitting}
            onChange={(e) => setPerOrder(e.target.value)}
            data-testid="whitelist-add-max_per_order_cents"
            className="w-40"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-pw-text">
          <span className="text-pw-text-muted">
            {t('whitelist.merchants.add.perDayLabel')}
          </span>
          <Input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={perDay}
            disabled={submitting}
            onChange={(e) => setPerDay(e.target.value)}
            data-testid="whitelist-add-max_per_day_cents"
            className="w-40"
          />
        </label>

        {anyBlank ? (
          <label className="flex items-center gap-2 text-xs text-pw-text">
            <input
              type="checkbox"
              checked={noLimitConfirmed}
              disabled={submitting}
              onChange={(e) =>
                setTicked(e.target.checked)
              }
              data-testid="whitelist-add-no-limit-confirm"
            />
            {t('whitelist.merchants.add.noLimitConfirm')}
          </label>
        ) : null}

        <Button
          type="button"
          disabled={submitting || !merchantId}
          onClick={() => void handleAdd()}
          data-testid="whitelist-add-submit"
        >
          {submitting ? <Spinner size="sm" /> : null}
          {submitting
            ? t('whitelist.merchants.add.submitting')
            : t('whitelist.merchants.add.submit')}
        </Button>
      </div>

      {error ? (
        <p
          className="mt-2 text-xs text-red-600 dark:text-red-300"
          role="alert"
          data-testid="whitelist-add-error"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

export default AddToWhitelistForm;
